import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/App";

const LINKS = [
  { label: "Home", href: "#home" },
  { label: "About Us", href: "#about" },
  { label: "Categories", href: "#categories" },
  { label: "Fabrics", href: "#fabrics" },
  { label: "Arabic Wear", href: "#arabic" },
  { label: "Solutions", href: "#business" },
  { label: "Global", href: "#global" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A0B0E]/85 border-b border-[#D4AF37]/20"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#home")}
          className="flex items-center gap-3 group"
        >
          <span className="w-10 h-10 border border-[#D4AF37]/60 flex items-center justify-center font-display text-lg text-[#D4AF37] group-hover:bg-[#D4AF37]/10 transition-colors duration-300">
            ATS
          </span>
          <span className="hidden sm:block text-left leading-tight">
            <span className="block font-display text-lg tracking-wide text-[#F8F5EE]">ATS Textile Trading</span>
            <span className="block text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/70">Dubai · Est. LLC</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => go(l.href)}
              className="text-xs tracking-[0.18em] uppercase text-slate-300 hover:text-[#D4AF37] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-rfq-button"
            onClick={() => go("#contact")}
            className="ml-2 px-5 py-2.5 border border-[#D4AF37]/70 text-[#D4AF37] text-xs tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#0A0B0E] transition-colors duration-300"
          >
            Request RFQ
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-[#F8F5EE]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-[#0A0B0E]/95 border-b border-[#D4AF37]/20"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => go(l.href)}
                  className="text-left font-display text-2xl text-slate-200 hover:text-[#D4AF37] transition-colors"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
